import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { chunk } from 'lodash-es';
import classnames from 'classnames';
import { StyledRobotTotalCleanedHourChart } from './RobotTotalCleanedHourChart.styles';
import { RobotTotalCleanedHourChartElement } from './RobotTotalCleanedHourChartElement/RobotTotalCleanedHourChartElement';
import { InfoTooltip } from 'lib/components/Tooltip/InfoTooltip/InfoTooltip';
import { InfoTooltipGlobalStyles } from 'lib/components/Tooltip/InfoTooltip/InfoTooltip.global.styles';
import { ReactComponent as UpArrow } from 'lib/assets/images/upArrow.svg';
import {
  RobotDashboardTotalCleanedHrsData,
  SortOrders,
} from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';
import { SortIcon } from 'app/components/SortIcon/SortIcon';
import { Tooltip } from 'lib/components/Tooltip/Tooltip';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';

export interface IRobotTotalCleanedHourChartProps {
  data: RobotDashboardTotalCleanedHrsData[];
}

const ELEMENTS_PER_PAGE = 10;

export const RobotTotalCleanedHourChart = ({ data }: IRobotTotalCleanedHourChartProps): JSX.Element => {
  const { t } = useTranslation();
  const [pageIndex, setPageIndex] = useState(0);
  const [sortOrder, setSortOrder] = useState<SortOrders>(SortOrders.Desc);

  const cleanedHoursMaximum = useMemo(
    () => Math.max(...data.map((datum: RobotDashboardTotalCleanedHrsData): number => datum.cleanedHrs ?? 0)),
    [data]
  );

  const sortedData = useMemo(() => {
    const sorterAscending = (a: RobotDashboardTotalCleanedHrsData, b: RobotDashboardTotalCleanedHrsData): number =>
      (a.cleanedHrs ?? 0) - (b.cleanedHrs ?? 0);
    const sorterDescending = (a: RobotDashboardTotalCleanedHrsData, b: RobotDashboardTotalCleanedHrsData): number =>
      sorterAscending(a, b) * -1;

    const sorterFn = sortOrder === SortOrders.Asc ? sorterAscending : sorterDescending;

    // We need to create a shallow copy of the data, so React updates the DOM after sorting. If we don't do this,
    // React doesn't know the contents of the array changed since it only checks for reference equality.
    return [...data].sort(sorterFn);
  }, [data, sortOrder]);

  const dataPages = useMemo(() => chunk(sortedData, ELEMENTS_PER_PAGE), [sortedData]);

  const isFirstPage = useMemo(() => pageIndex === 0, [pageIndex]);
  const isLastPage = useMemo(() => pageIndex === dataPages.length - 1, [dataPages.length, pageIndex]);

  const handlePrevious = (): void => setPageIndex(index => Math.max(0, index - 1));
  const handleNext = (): void => setPageIndex(index => Math.min(index + 1, dataPages.length - 1));

  const handleSortOrderChange = (): void =>
    setSortOrder(currentSortOrder => {
      setPageIndex(0);

      if (currentSortOrder === SortOrders.Asc) {
        return SortOrders.Desc;
      }

      return SortOrders.Asc;
    });

  return (
    <StyledRobotTotalCleanedHourChart className="robot-total-cleaned-hour-chart">
      <InfoTooltipGlobalStyles />
      <h4 className="robot-total-cleaned-hour-chart__title-bar">
        <span className="robot-total-cleaned-hour-chart__title">
          {t('robotDashboard.totalCleanedHourChart.title')}
          <div
            className={classnames('robot-total-cleaned-hour-chart__sort-order-button')}
            onClick={handleSortOrderChange}
          >
            <SortIcon sortOrder={sortOrder} />
          </div>
        </span>
        <Tooltip
          title={<InfoTooltip content="robotDashboard.totalCleanedHourChart.tooltip" />}
          overlayClassName="tooltip-overlay"
          placement="bottomRight"
        >
          <div>
            <SvgIcon name="info" className="robot-dashboard__info-icon" />
          </div>
        </Tooltip>
      </h4>
      <div
        onClick={handlePrevious}
        className={classnames(
          'robot-total-cleaned-hour-chart__previous-button',
          'robot-total-cleaned-hour-chart__pagination-button',
          { 'robot-total-cleaned-hour-chart__pagination-button--disabled': isFirstPage }
        )}
      >
        <UpArrow />
      </div>
      <div className="robot-total-cleaned-hour-chart__elements">
        {dataPages[pageIndex] &&
          dataPages[pageIndex].map((datum, index) => (
            <RobotTotalCleanedHourChartElement
              className="robot-total-cleaned-hour-chart__element"
              // While it is usually bad practice to use an index as a collection key in React and should be avoided
              // we doing it here on purpose so DOM elements are reused and the transition animation is applied
              // when changing pages or sorting
              keyIndex={`robot-total-cleaned-hour-chart__element-index-${index}`}
              totalCleanedHoursDatum={datum}
              cleanedHoursMaximum={cleanedHoursMaximum}
              key={`robot-total-cleaned-hour-chart__element-index-${index}`}
            />
          ))}
      </div>
      <div
        onClick={handleNext}
        className={classnames(
          'robot-total-cleaned-hour-chart__next-button',
          'robot-total-cleaned-hour-chart__pagination-button',
          { 'robot-total-cleaned-hour-chart__pagination-button--disabled': isLastPage }
        )}
      >
        <UpArrow className="robot-total-cleaned-hour-chart__next-button-icon" />
      </div>
    </StyledRobotTotalCleanedHourChart>
  );
};
